import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, XCircle, UserCheck, AlertCircle } from "lucide-react";

interface PendingProfile {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  bio: string | null;
  invite_email: string;
}

export default function ClaimProfile() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [profile, setProfile] = useState<PendingProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [isEighteenPlus, setIsEighteenPlus] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setError("Invalid claim link");
        setLoading(false);
        return;
      }

      try {
        // Use secure edge function to validate token and get minimal profile data
        const { data, error: fnError } = await supabase.functions.invoke('validate-claim-token', {
          body: { token }
        });

        if (fnError) {
          console.error("claim_profile.validate_token_failed");
          throw new Error("Failed to validate claim link");
        }

        if (!data.success || !data.valid) {
          if (data.error === 'Invalid or expired claim link') {
            setAlreadyClaimed(true);
            setError("This claim link is invalid or has already been used");
          } else {
            setError(data.error || "Invalid claim link");
          }
          setLoading(false);
          return;
        }

        // Check if current user already has a profile
        if (user) {
          const { data: existingProfile } = await supabase
            .from("profiles")
            .select("id")
            .eq("id", user.id)
            .eq("status", "active")
            .maybeSingle();

          if (existingProfile) {
            setError("You already have an active profile. Each user can only have one profile.");
            setLoading(false);
            return;
          }
        }

        setProfile(data.profile);
      } catch (err: any) {
        console.error("claim_profile.load_failed");
        setError(err.message || "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token, user]);

  const handleClaim = async () => {
    if (!profile || !user) {
      toast.error("You must be logged in to claim this profile");
      navigate(`/login?returnTo=/claim-profile/${token}`);
      return;
    }

    setClaiming(true);

    try {
      // Use service role through authenticated request - user must be logged in with correct email
      // Fetch the full pending profile data (user is authenticated, RLS will check ownership)
      const { data: pendingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", profile.id)
        .single();

      if (fetchError) throw fetchError;

      // Fetch contact info for the pending profile
      const { data: contactInfo, error: contactError } = await supabase
        .from("contact_info")
        .select("*")
        .eq("profile_id", profile.id)
        .maybeSingle();

      if (contactError) console.error("claim_profile.fetch_contact_failed");

      // Create or update profile with user's actual ID
      const { error: insertError } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          first_name: pendingProfile.first_name,
          age: pendingProfile.age,
          gender: pendingProfile.gender,
          location: pendingProfile.location,
          bio: pendingProfile.bio,
          education_level: pendingProfile.education_level,
          occupation: pendingProfile.occupation,
          religiosity_level: pendingProfile.religiosity_level,
          prayer_frequency: pendingProfile.prayer_frequency,
          ethnicity: pendingProfile.ethnicity,
          citizenship_status: pendingProfile.citizenship_status,
          marital_status: pendingProfile.marital_status,
          sect: pendingProfile.sect,
          masjid_attendance: pendingProfile.masjid_attendance,
          owned_by: user.id,
          status: "active",
          claimed_at: new Date().toISOString(),
          is_active: true,
          profile_completed: pendingProfile.profile_completed || false,
          seeking_age_min: pendingProfile.seeking_age_min,
          seeking_age_max: pendingProfile.seeking_age_max,
          wears_hijab: pendingProfile.wears_hijab,
          height_feet: pendingProfile.height_feet,
          height_inches: pendingProfile.height_inches,
          has_children: pendingProfile.has_children,
          children_count: pendingProfile.children_count,
          willing_to_relocate: pendingProfile.willing_to_relocate,
          importance_of_islam: pendingProfile.importance_of_islam,
          personality_type: pendingProfile.personality_type,
          financial_status: pendingProfile.financial_status,
          quran_knowledge: pendingProfile.quran_knowledge,
          islamic_knowledge_level: pendingProfile.islamic_knowledge_level,
          seeking_preferences_weights: pendingProfile.seeking_preferences_weights,
        });

      if (insertError) {
        console.error("claim_profile.upsert_failed");
        throw insertError;
      }

      // Copy contact info if it exists
      if (contactInfo) {
        const { error: contactInsertError } = await supabase
          .from("contact_info")
          .insert({
            profile_id: user.id,
            email: contactInfo.email,
            phone: contactInfo.phone,
            whatsapp: contactInfo.whatsapp,
            preferred_contact_method: contactInfo.preferred_contact_method,
          });

        if (contactInsertError) {
          console.error("claim_profile.copy_contact_failed");
        }
      }

      // Delete the pending profile
      const { error: deleteError } = await supabase
        .from("profiles")
        .delete()
        .eq("id", profile.id);

      if (deleteError) {
        console.error("claim_profile.delete_pending_failed");
      }

      toast.success("Profile claimed successfully!");
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate("/dashboard");
    } catch (err: any) {
      console.error("claim_profile.claim_failed");
      toast.error(err.message || "Failed to claim profile");
    } finally {
      setClaiming(false);
    }
  };

  const handleDecline = async () => {
    if (!profile) return;

    const confirmed = window.confirm(
      "Are you sure you want to decline this profile? This action cannot be undone."
    );

    if (!confirmed) return;

    setClaiming(true);

    try {
      // Update profile status to declined via the edge function or direct update
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          status: "declined",
          claim_token: null,
        })
        .eq("id", profile.id);

      if (updateError) {
        console.error("claim_profile.decline_update_failed");
        throw updateError;
      }

      toast.success("Profile declined");
      navigate("/");
    } catch (err: any) {
      console.error("claim_profile.decline_failed");
      toast.error(err.message || "Failed to decline profile");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Validating claim link...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                {alreadyClaimed ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <XCircle className="h-8 w-8 text-destructive" />
                )}
                <CardTitle>{alreadyClaimed ? "Already Claimed" : "Invalid Link"}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <Alert variant={alreadyClaimed ? "default" : "destructive"}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="mt-6">
                <Button onClick={() => navigate("/")} className="w-full">
                  Go to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="h-8 w-8 text-primary" />
                <CardTitle>Claim Your Profile</CardTitle>
              </div>
              <CardDescription>
                A profile has been created for you. Please log in or sign up to claim it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You need to be logged in with the email <strong>{profile.invite_email}</strong> to claim this profile.
                  If you don't have an account yet, please ask your matchmaker to send you an invitation.
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate(`/login?returnTo=/claim-profile/${token}`)}
                  className="w-full"
                >
                  Log In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Check if logged in with correct email
  if (user.email !== profile.invite_email) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="h-8 w-8 text-amber-500" />
                <CardTitle>Email Mismatch</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This profile was created for <strong>{profile.invite_email}</strong>, but you're logged in as <strong>{user.email}</strong>.
                  Please log in with the correct email address.
                </AlertDescription>
              </Alert>
              <div className="mt-6">
                <Button onClick={() => navigate("/login")} className="w-full">
                  Log In with Different Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Show profile preview and claim options
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 container mx-auto px-4 py-12 max-w-3xl">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <UserCheck className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Claim Your Profile</h1>
          <p className="text-muted-foreground">
            A matchmaker has created this profile for you. Review the details below.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Profile Preview</CardTitle>
            <CardDescription>This information was provided by your matchmaker</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Name</span>
                <p className="font-medium">{profile.first_name}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Age</span>
                <p className="font-medium">{profile.age}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Gender</span>
                <p className="font-medium capitalize">{profile.gender}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Location</span>
                <p className="font-medium">{profile.location || "Not specified"}</p>
              </div>
            </div>

            {profile.bio && (
              <div>
                <span className="text-sm text-muted-foreground">Bio</span>
                <p className="mt-1">{profile.bio}</p>
              </div>
            )}

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                After claiming, you can edit and complete your profile in your dashboard.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <div className="mb-6 flex items-start space-x-2 rounded-md border border-border bg-muted/30 p-4">
          <Checkbox
            id="claim_is_eighteen_plus"
            checked={isEighteenPlus}
            onCheckedChange={(checked) => setIsEighteenPlus(checked === true)}
          />
          <Label
            htmlFor="claim_is_eighteen_plus"
            className="font-normal cursor-pointer leading-snug"
          >
            I confirm I am 18 years of age or older. MAS Matrimonial is for
            adults seeking marriage.
          </Label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleClaim}
            disabled={claiming || !isEighteenPlus}
            className="flex-1"
            size="lg"
          >
            {claiming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Claiming...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Claim & Continue to Dashboard
              </>
            )}
          </Button>
          <Button
            onClick={handleDecline}
            disabled={claiming}
            variant="outline"
            size="lg"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Decline
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
